import React, { useEffect, useState, useCallback } from "react";
import { CalendarClock, Loader2, RefreshCw, LogOut } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Ops-side self attendance card — lets field decorators/ops managers see their
 * punch-in times and hours logged for the last 30 days + explicit check-out button.
 */
const OpsAttendanceCard = ({ authAxios }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  const fetchMe = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/ops/attendance/me`, { params: { days: 30 } });
      setRows(r.data?.items || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const checkOut = async () => {
    setCheckingOut(true);
    try {
      await authAxios.post(`${API_URL}/api/ops/attendance/checkout`, {});
      await fetchMe();
    } catch { /* silent */ }
    setCheckingOut(false);
  };

  const today = (rows && rows[0] && rows[0].date === new Date().toISOString().slice(0, 10)) ? rows[0] : null;
  const totalHours = rows.reduce((s, r) => s + (r.hours || 0), 0);
  const presentDays = rows.filter((r) => r.status === "present" || r.status === "wfh").length;

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="ops-attendance-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
          <CalendarClock size={16} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-[#1E1B4B] text-sm">My Attendance</h3>
          <p className="text-[11px] text-[#94a3b8]">Last 30 days · auto-closes after 30 min idle</p>
        </div>
        <button onClick={fetchMe} className="p-1.5 rounded-lg hover:bg-gray-100" title="Refresh">
          <RefreshCw size={13} className="text-gray-400" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-emerald-50 rounded-xl px-2 py-2 text-center">
          <p className="text-[10px] text-emerald-700 uppercase font-bold">Today</p>
          <p className="text-sm font-bold text-emerald-700">{today ? `${today.hours ?? 0}h` : "—"}</p>
        </div>
        <div className="bg-indigo-50 rounded-xl px-2 py-2 text-center">
          <p className="text-[10px] text-indigo-700 uppercase font-bold">Present</p>
          <p className="text-sm font-bold text-indigo-700">{presentDays}d</p>
        </div>
        <div className="bg-amber-50 rounded-xl px-2 py-2 text-center">
          <p className="text-[10px] text-amber-700 uppercase font-bold">Total</p>
          <p className="text-sm font-bold text-amber-700">{totalHours.toFixed(1)}h</p>
        </div>
      </div>

      {today && !today.logout_at && (
        <button
          onClick={checkOut}
          disabled={checkingOut}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#1E1B4B] text-white text-xs font-bold hover:opacity-90 disabled:opacity-60 mb-3"
          data-testid="ops-checkout-btn"
        >
          {checkingOut ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />}
          Check out now
        </button>
      )}

      {loading ? (
        <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-[#E83E8C]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-[11px] text-[#94a3b8] text-center py-3">No attendance recorded yet.</p>
      ) : (
        <div className="max-h-56 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-gray-50 text-gray-500 uppercase">
              <tr>
                <th className="px-2 py-1 text-left">Date</th>
                <th className="px-2 py-1 text-left">In</th>
                <th className="px-2 py-1 text-left">Out</th>
                <th className="px-2 py-1 text-right">Hrs</th>
                <th className="px-2 py-1 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100" data-testid={`ops-attendance-row-${r.date}`}>
                  <td className="px-2 py-1 font-mono">{r.date}</td>
                  <td className="px-2 py-1">{(r.login_at || "").slice(11, 16)}</td>
                  <td className="px-2 py-1">{(r.logout_at || "").slice(11, 16) || <span className="text-emerald-600">—</span>}</td>
                  <td className="px-2 py-1 text-right font-semibold">{r.hours ?? "-"}</td>
                  <td className="px-2 py-1 capitalize">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OpsAttendanceCard;
